import { motion } from 'framer-motion';
import AntButton from '../ui/AntButton';
import { personalInfo } from '../../data/portfolioData';
import { scrollToSection } from '../../utils/scrollTo';

const footerLinks = [
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'projects', label: 'Projects' },
  { id: 'contact', label: 'Contact' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-ant-bg text-ant-fg border-t border-ant-line-soft">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-12 sm:py-14 border-b border-white/[0.06]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="ant-card ant-ring ant-glass rounded-2xl border border-white/[0.08] bg-white/[0.02] p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6"
          >
            <div className="max-w-xl">
              <p className="font-mono text-[10px] tracking-widest uppercase text-ant-gold/70 mb-3">
                Available for contract
              </p>
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2">
                Ready to ship your next product?
              </h3>
              <p className="text-sm text-white/40 leading-relaxed">
                Contract and freelance engagements — full-stack delivery from architecture through production.
              </p>
            </div>
            <AntButton onClick={() => scrollToSection('contact')} className="shrink-0 w-full sm:w-auto">
              Start a project
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </AntButton>
          </motion.div>
        </div>

        <div className="py-12 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 sm:gap-12 mb-10 sm:mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center">
                  <span className="font-mono text-[11px] font-bold text-ant-gold">GS</span>
                </div>
                <span className="text-sm font-semibold text-white/90">{personalInfo.name}</span>
              </div>
              <p className="text-sm text-white/35 leading-relaxed max-w-xs">
                {personalInfo.tagline}
              </p>
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-widest uppercase text-white/25 mb-4">Sections</p>
              <ul className="space-y-2">
                {footerLinks.map((link) => (
                  <li key={link.id}>
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-sm text-white/40 hover:text-white/70 transition-colors duration-200 cursor-pointer"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
                <li>
                  <a
                    href={personalInfo.resumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/40 hover:text-white/70 transition-colors duration-200"
                  >
                    Resume
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-widest uppercase text-white/25 mb-4">Contact</p>
              <div className="space-y-2 text-sm text-white/40">
                <a href={personalInfo.social.email} className="block hover:text-white/60 transition-colors duration-200 break-all">
                  {personalInfo.email}
                </a>
                <p>{personalInfo.location}</p>
              </div>
              <div className="flex items-center gap-4 mt-5">
                <a href={personalInfo.social.github} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white/60 transition-colors duration-200 p-2 -ml-2" aria-label="GitHub">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.865 8.167 6.84 9.49.5.094.68-.217.68-.48 0-.237-.008-.866-.013-1.7-2.782.604-3.365-1.34-3.365-1.34-.454-1.155-1.11-1.464-1.11-1.464-.908-.62.07-.608.07-.608 1.004.07 1.53 1.03 1.53 1.03.89 1.525 2.34 1.085 2.91.83.093-.645.35-1.086.635-1.336-2.22-.25-4.555-1.11-4.555-4.937 0-1.09.39-1.98 1.03-2.68-.103-.25-.448-1.27.097-2.647 0 0 .84-.27 2.75 1.024A9.57 9.57 0 0112 6.763c.855.004 1.715.116 2.517.345 1.91-1.295 2.75-1.024 2.75-1.024.546 1.377.2 2.397.1 2.647.64.7 1.03 1.59 1.03 2.68 0 3.837-2.34 4.687-4.57 4.937.36.31.68.918.68 1.848 0 1.335-.012 2.41-.012 2.735 0 .265.18.575.688.48C19.135 20.167 22 16.42 22 12c0-5.52-4.48-10-10-10z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href={personalInfo.social.linkedin} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white/60 transition-colors duration-200 p-2" aria-label="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="pt-6 sm:pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row justify-between items-center gap-3 text-center sm:text-left">
            <p className="text-xs font-mono text-white/25">
              © {currentYear} {personalInfo.name}
            </p>
            <p className="text-xs font-mono text-white/20">
              Built with React · Deployed on Vercel
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;